import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ArrowRight, Radar, Brain, Sparkles, FileText, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = [
  {
    key: "discover",
    eyebrow: "01 · DISCOVER",
    title: "Score every SAM.gov opportunity in seconds",
    body: "BidForge pulls live from SAM.gov and scores every notice against your NAICS, service areas, certifications, and set-aside profile — no manual filtering, no spreadsheets.",
    screenshot: "/product-screens/opportunities.jpg",
    icon: Radar,
    tone: "indigo",
  },
  {
    key: "decide",
    eyebrow: "02 · DECIDE",
    title: "Bid or no-bid with a defensible AI briefing",
    body: "One click drafts an executive briefing with win probability, risk register, scope of work, materials, bonding, and compliance requirements — plus a Why-this-score breakdown you can trust.",
    screenshot: "/product-screens/dashboard.jpg",
    icon: Brain,
    tone: "violet",
  },
  {
    key: "price",
    eyebrow: "03 · PRICE",
    title: "Estimate against real market benchmarks",
    body: "Services or materials mode. Loaded rates, USAspending benchmarks, peer wins calibration, past-performance auto-cite. Basis-of-Estimate traces every line back to the RFP.",
    screenshot: "/product-screens/forecaster.jpg",
    icon: TrendingUp,
    tone: "emerald",
  },
  {
    key: "propose",
    eyebrow: "04 · PROPOSE",
    title: "Generate the entire binder — cover to signature",
    body: "14-section proposal drafted section-by-section against your workspace's house-style skill. Submittal binder auto-assembled with real SDS, OSHA, insurance, and signature PDFs.",
    screenshot: "/product-screens/connect.jpg",
    icon: FileText,
    tone: "fuchsia",
  },
  {
    key: "win",
    eyebrow: "05 · WIN",
    title: "Teaming, subs, and follow-through built in",
    body: "SBA subcontractor search, Prime auto-match, USAspending competitor benchmarks, Connect CRM with nudge sequences, and Auto-Agent watches your pipeline while you sleep.",
    screenshot: "/product-screens/connect.jpg",
    icon: Sparkles,
    tone: "amber",
  },
];

const TONE = {
  indigo:  { text: "text-indigo-700",  bg: "bg-indigo-50",  border: "border-indigo-200",  bar: "bg-indigo-500" },
  violet:  { text: "text-violet-700",  bg: "bg-violet-50",  border: "border-violet-200",  bar: "bg-violet-500" },
  emerald: { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", bar: "bg-emerald-500" },
  fuchsia: { text: "text-fuchsia-700", bg: "bg-fuchsia-50", border: "border-fuchsia-200", bar: "bg-fuchsia-500" },
  amber:   { text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200",   bar: "bg-amber-500" },
};

export default function LandingDemo({ open, onClose }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setI((v) => Math.min(STEPS.length - 1, v + 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => { if (open) setI(0); }, [open]);

  if (!open) return null;
  const step = STEPS[i];
  const tone = TONE[step.tone];
  const Icon = step.icon;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8" data-testid="landing-demo">
      <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-200" data-testid="landing-demo-panel">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 border border-zinc-200 flex items-center justify-center text-zinc-500 hover:text-zinc-900"
          data-testid="landing-demo-close"
          aria-label="Close demo"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress bar */}
        <div className="h-1.5 bg-zinc-100">
          <div className={`h-full ${tone.bar} transition-all duration-500`} style={{ width: `${((i + 1) / STEPS.length) * 100}%` }} />
        </div>

        <div className="grid md:grid-cols-2">
          {/* Left: copy */}
          <div className="p-8 sm:p-10 flex flex-col">
            <div className={`inline-flex items-center gap-2 self-start px-3 py-1 rounded-full border ${tone.border} ${tone.bg} ${tone.text} text-[10px] font-mono uppercase tracking-widest mb-4`}>
              <Icon className="w-3.5 h-3.5" />
              {step.eyebrow}
            </div>
            <h2 className="font-bold text-2xl sm:text-3xl tracking-tight text-zinc-900 leading-tight mb-4" style={{ fontFamily: "'Chivo', sans-serif" }} data-testid="landing-demo-title">
              {step.title}
            </h2>
            <p className="text-zinc-600 leading-relaxed text-base mb-8">{step.body}</p>

            <div className="mt-auto space-y-4">
              <div className="flex items-center gap-2" data-testid="landing-demo-dots">
                {STEPS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    className={`h-2 rounded-full transition-all ${idx === i ? `w-8 ${tone.bar}` : "w-2 bg-zinc-200"}`}
                    aria-label={`Go to step ${idx + 1}`}
                    data-testid={`landing-demo-dot-${idx}`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setI((v) => Math.max(0, v - 1))}
                  disabled={i === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900 disabled:opacity-30"
                  data-testid="landing-demo-prev"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>

                {i < STEPS.length - 1 ? (
                  <button
                    onClick={() => setI((v) => v + 1)}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-700"
                    data-testid="landing-demo-next"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <Link
                    to="/signup"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white text-sm font-medium shadow-lg shadow-indigo-500/25 hover:shadow-xl"
                    data-testid="landing-demo-signup"
                  >
                    Start free trial <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Right: screenshot */}
          <div className={`relative min-h-[340px] ${tone.bg} p-6 sm:p-8 flex items-center justify-center border-l border-zinc-200`}>
            <div className="w-full rounded-xl overflow-hidden border border-zinc-200 shadow-xl bg-white">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                <div className="ml-3 text-[10px] font-mono text-zinc-500 truncate">bidforgeai.org / {step.key}</div>
              </div>
              <img
                src={step.screenshot}
                alt={step.title}
                className="w-full h-auto object-cover"
                data-testid={`landing-demo-screenshot-${i}`}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
